"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import {
  MobileBottomToolRegistration,
  type MobileBottomToolItem,
} from "@/components/mobile-bottom-tools";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { PASS_FEATURES } from "@/types/pass";
import {
  getPageBookmarkState,
  setPageBookmark,
} from "@/lib/server-actions/pageBookmarkActions";

// 페이지 즐겨찾기 토글 (JCG-3000). 스크롤 위치와 관계없이 항상 보이는 두 자리에 노출한다.
//   - 데스크탑 셸(≥1024px): 상단 바(ContentHeader) 우측 액션 슬롯에 portal — LanguageToggle 과 같은 방식
//   - 클래식 레이아웃(<1024px): 하단 도구 Dock 의 action 도구(필기 10 과 출석 20 사이)
// 비로그인은 로그인 안내, Starter 미만은 이용권 안내 토스트를 띄운다(버튼은 숨기지 않는다).

const BOOKMARKS_PAGE = "/my/bookmarks";

interface PageBookmarkProps {
  pagePath: string;
}

export default function PageBookmark({ pagePath }: PageBookmarkProps) {
  const router = useRouter();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: passLoaded, hasAccess } = useEffectivePass();
  const { inShell, headerActionSlot } = useArticleShellSlots();
  const [bookmarked, setBookmarked] = useState(false);
  const [stateLoaded, setStateLoaded] = useState(false);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);

  const canUse = passLoaded && hasAccess(PASS_FEATURES.pageBookmark);

  // 로그인 사용자는 이용권과 관계없이 저장 상태를 보여준다(이용권이 끝나도 저장한 표시는 남는다).
  useEffect(() => {
    if (!authLoaded) return;
    if (!isSignedIn) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBookmarked(false);
      setStateLoaded(true);
      return;
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    setStateLoaded(false);
    // 페이지 진입 직후 다른 server action 과 겹치면 요청이 중단(reject)될 수 있어 한 번 더 조회한다.
    // 그래도 실패하면 저장 안 됨으로 보여준다 — 저장(upsert 무시)·해제(delete)가 멱등이라 잘못 눌러도 안전하다.
    const load = (attempt: number) => {
      getPageBookmarkState(pagePath)
        .then(({ bookmarked: next }) => {
          if (cancelled) return;
          setBookmarked(next);
          setStateLoaded(true);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt === 0) {
            retryTimer = setTimeout(() => load(1), 1000);
            return;
          }
          setStateLoaded(true);
        });
    };
    load(0);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [authLoaded, isSignedIn, pagePath]);

  const toggle = useCallback(async () => {
    if (!authLoaded || pendingRef.current) return;

    if (!isSignedIn) {
      toast("로그인하면 이 페이지를 즐겨찾기할 수 있어요.", {
        action: {
          label: "로그인",
          onClick: () =>
            router.push(`/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`),
        },
      });
      return;
    }
    const next = !bookmarked;
    // 이용권은 추가할 때만 막는다. 이용권이 끝나도 저장해 둔 즐겨찾기는 해제할 수 있다.
    if (next) {
      if (!passLoaded) return;
      if (!canUse) {
        toast("즐겨찾기는 Starter 이상 이용권에서 사용할 수 있어요.", {
          action: { label: "이용권 보기", onClick: () => router.push("/membership") },
        });
        return;
      }
    }

    pendingRef.current = true;
    setPending(true);
    setBookmarked(next);
    try {
      const result = await setPageBookmark(pagePath, next);
      if (!result.success) {
        setBookmarked(!next);
        toast.error(result.error);
        return;
      }
      if (result.bookmarked) {
        toast.success("즐겨찾기에 추가했어요.", {
          action: { label: "모아보기", onClick: () => router.push(BOOKMARKS_PAGE) },
        });
      } else {
        toast("즐겨찾기에서 뺐어요.");
      }
    } catch {
      setBookmarked(!next);
      toast.error("즐겨찾기를 저장하지 못했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  }, [authLoaded, bookmarked, canUse, isSignedIn, pagePath, passLoaded, router]);

  const label = bookmarked ? "즐겨찾기 해제" : "즐겨찾기에 추가";

  const mobileTool = useMemo<MobileBottomToolItem>(
    () => ({
      id: "page-bookmark",
      kind: "action",
      label: "즐겨찾기",
      order: 15,
      // 저장 중에는 스피너 대신 바뀐 별을 바로 보여주고, 중복 클릭은 pendingRef 로 막는다.
      selected: bookmarked,
      loading: !stateLoaded,
      ariaLabel: label,
      renderIcon: ({ className, selected }) => (
        <Star className={cn(className, selected && "fill-current")} aria-hidden="true" />
      ),
      onAction: toggle,
    }),
    [bookmarked, label, stateLoaded, toggle],
  );

  if (inShell) {
    if (!headerActionSlot) return null;
    return createPortal(
      <button
        type="button"
        onClick={toggle}
        disabled={!stateLoaded}
        aria-busy={pending || undefined}
        title={label}
        aria-label={label}
        aria-pressed={bookmarked}
        className={cn(
          // 우측 "도구" 토글과 같은 크기·글꼴(아이콘 + 라벨). order-last 로 슬롯 맨 오른쪽에 둬서
          // 언어 토글 유무·포털 순서와 관계없이 즐겨찾기·검색·도구 버튼이 항상 같은 자리에 있게 한다.
          "order-last inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border px-2 text-[12px] font-bold transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-wait",
          bookmarked
            ? "border-amber-300 bg-amber-50 text-amber-600 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
            : "border-border bg-card text-muted-foreground hover:text-foreground",
        )}
      >
        <Star className={cn("h-[17px] w-[17px]", bookmarked && "fill-current")} aria-hidden="true" />
        <span>즐겨찾기</span>
      </button>,
      headerActionSlot,
    );
  }

  return <MobileBottomToolRegistration tool={mobileTool} />;
}
